/**
 * Lowest age that may hold an account: the digital age of consent in Austria
 * (§ 4 Abs. 4 DSG), and the floor for every country. Accounts without a
 * country keep it; everyone else goes through {@link minimumAgeFor}.
 */
export const MINIMUM_AGE_YEARS = 14

/**
 * Countries above the floor, by their digital age of consent (GDPR Art. 8 as
 * each member state set it). Every other country, including those that allow
 * 13 and those outside the GDPR, uses {@link MINIMUM_AGE_YEARS}.
 */
const CONSENT_AGE_ABOVE_FLOOR: Readonly<Record<string, number>> = {
  DE: 16,
  HR: 16,
  HU: 16,
  IE: 16,
  LI: 16,
  LU: 16,
  NL: 16,
  PL: 16,
  RO: 16,
  SK: 16,
  CZ: 15,
  FR: 15,
  GR: 15,
  SI: 15,
}

/** Countries offered at sign-up (ISO 3166-1 alpha-2), Austria and its neighbours first. */
export const SIGNUP_COUNTRIES = [
  'AT',
  'DE',
  'CH',
  'IT',
  'SI',
  'HR',
  'BA',
  'RS',
  'HU',
  'CZ',
  'SK',
  'PL',
  'RO',
  'BG',
  'ME',
  'MK',
  'AL',
  'XK',
  'GR',
  'CY',
  'MT',
  'ES',
  'PT',
  'FR',
  'BE',
  'NL',
  'LU',
  'LI',
  'IE',
  'GB',
  'DK',
  'SE',
  'NO',
  'FI',
  'IS',
  'EE',
  'LV',
  'LT',
  'UA',
  'MD',
  'TR',
] as const

export type SignupCountry = (typeof SIGNUP_COUNTRIES)[number]

/** Whether a code is one of {@link SIGNUP_COUNTRIES}. */
export function isSignupCountry(code: string | null | undefined): code is SignupCountry {
  return !!code && (SIGNUP_COUNTRIES as readonly string[]).includes(code.toUpperCase())
}

/** The lowest age that may hold an account for someone living in `country`. */
export function minimumAgeFor(country: string | null | undefined): number {
  return CONSENT_AGE_ABOVE_FLOOR[country?.toUpperCase() ?? ''] ?? MINIMUM_AGE_YEARS
}

/**
 * Age of majority. Below it an account is treated as a minor: kept out of
 * search, suggestions and contact matching, barred from public presence, and
 * held for owner approval when joining a spot by invite.
 *
 * Distinct from {@link MINIMUM_AGE_YEARS} — that one is who may hold an
 * account at all, this one is who is exposed to strangers. 18 is near-universal
 * for majority, so unlike the consent age it is not expected to vary by market.
 */
export const ADULT_AGE_YEARS = 18

/**
 * Lowest age for open visibility in a Live Room: seen by strangers checked in at the same event
 * and reachable by their Hi. Younger people choose only between hidden and friends.
 */
export const OPEN_PRESENCE_AGE_YEARS = 16

/** A date of birth stays editable this long after it was first set ... */
export const DATE_OF_BIRTH_EDIT_WINDOW_DAYS = 3
/** ... and for at most this many corrections, whichever runs out first. */
export const DATE_OF_BIRTH_MAX_CHANGES = 3

interface CalendarDate {
  year: number
  month: number
  day: number
}

/** Parses `YYYY-MM-DD`; null when malformed or not a real calendar day. */
export function parseIsoDate(value: string): CalendarDate | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const probe = new Date(Date.UTC(year, month - 1, day))
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    return null
  }
  return { year, month, day }
}

/** True when the person born on `dateOfBirth` has had their `years`th birthday by the local day of `at`. */
export function isAtLeastAge(dateOfBirth: string, years: number, at: Date = new Date()): boolean {
  const birth = parseIsoDate(dateOfBirth)
  if (!birth) return false
  const birthday = Date.UTC(birth.year + years, birth.month - 1, birth.day)
  const today = Date.UTC(at.getFullYear(), at.getMonth(), at.getDate())
  return today >= birthday
}

export interface DateOfBirthEditState {
  editable: boolean
  /** End of the edit window as ISO timestamp; null while nothing has been set yet. */
  editableUntil: string | null
  changesLeft: number
}

interface DateOfBirthFields {
  dateOfBirth: string | null
  dateOfBirthSetAt: string | null
  dateOfBirthChanges: number
}

/** Whether the date of birth may still be changed, given when it was first set and how often since. */
export function dateOfBirthEditState(
  profile: DateOfBirthFields,
  now: Date = new Date(),
): DateOfBirthEditState {
  const changesLeft = Math.max(DATE_OF_BIRTH_MAX_CHANGES - profile.dateOfBirthChanges, 0)
  if (!profile.dateOfBirth || !profile.dateOfBirthSetAt) {
    return { editable: true, editableUntil: null, changesLeft: DATE_OF_BIRTH_MAX_CHANGES }
  }
  const until = new Date(
    new Date(profile.dateOfBirthSetAt).getTime() + DATE_OF_BIRTH_EDIT_WINDOW_DAYS * 86_400_000,
  )
  return {
    editable: now < until && changesLeft > 0,
    editableUntil: until.toISOString(),
    changesLeft,
  }
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

/** Whether a calendar day (`YYYY-MM-DD`) is the birthday; 29 February counts on 28 February in other years. */
export function isBirthdayOn(dateOfBirth: string, isoDate: string): boolean {
  const [, month, day] = dateOfBirth.split('-').map(Number)
  const [year, todayMonth, todayDay] = isoDate.split('-').map(Number)
  if (month === todayMonth && day === todayDay) return true
  return month === 2 && day === 29 && todayMonth === 2 && todayDay === 28 && !isLeapYear(year)
}

/** Whole years on a calendar day (`YYYY-MM-DD`); a 29 February birthday counts on 28 February. */
export function ageOn(dateOfBirth: string, isoDate: string): number {
  const [birthYear, birthMonth, birthDay] = dateOfBirth.split('-').map(Number)
  const [year, month, day] = isoDate.split('-').map(Number)
  const reached =
    month > birthMonth ||
    (month === birthMonth && day >= birthDay) ||
    isBirthdayOn(dateOfBirth, isoDate)
  return year - birthYear - (reached ? 0 : 1)
}
